// @flow
import * as React from 'react';
import cx from 'classnames';
import classNames from 'classnames/bind';
import { pathOr } from 'ramda';
import Checkbox from '../../basic-components/checkbox';
import Row from '../../basic-components/table/row';
import Cell from '../../basic-components/table/cell';
import Icon from '../../basic-components/icon';
import ClickToCopy from '../../basic-components/click-to-copy';
import Thumbnail from '../../basic-components/thumbnail';
import Marker from '../../basic-components/marker';
import AiModalPublishSwitcher from './ai-modal-publish-switcher';
import Problem from '../stock-table/problem';
import VariationRow from './variation-row';
import { ellipsisize } from '../../utils/string';
import catchMouseFocus from '../../utils/hocs/catch-mouse-focus';
import { flex } from '../stock-table/flexconfig';
import cs from './styles.pcss';

import type {
  Products_products_nodes as ProductT,
  StockData_showcases as ShowcaseT,
  StockData_profiles_nodes as ShippingProfileType,
} from '../../typings/product-query.flow';
import type { ProductChange } from '../../typings/product';

import t from './locale';

const csx = classNames.bind(cs);

const ThumbButton = catchMouseFocus((props) => (
  <button type="button" {...props} />
));

// 从 stock-row/index.js 复制的逻辑
const showWarningIcon = (listing) => {
  let hasWarningOnly = false;
  let hasError = false;

  if (listing?.problems && listing?.problems.length) {
    hasError = listing.problems.some((p) => p.severity === 'error');
    const onlyHasWarning = listing.problems.every(
      (p) => p.severity === 'warning'
    );
    const isBeing = !listing.published && listing.selected;
    const isPreparePublish = !listing.published && !listing.selected;
    hasWarningOnly =
      isBeing && onlyHasWarning && !isPreparePublish && !listing.published;
  }

  return { hasError, hasWarningOnly };
};

const getSku = (variations) => {
  const sku = pathOr<string, string | number, string, any>(
    '',
    [0, 'sku'],
    variations
  );
  if (variations && variations.length > 1) return '';
  return sku;
};

type Props = {
  item: ProductT,
  showcases: Array<ShowcaseT>,
  shippingProfiles?: ShippingProfileType[],
  checkedVariations?: string[],
  openedVariations?: string[],
  onToggle: (id: string, variationsIds: string[]) => void,
  onVariationToggle?: (id: string) => void,
  onVariationsOpenToggle?: (id: string) => void,
  onClick?: (id: string) => void,
  checkedProducts?: string[],
  changes?: ProductChange[],
  changeSuccessIds?: string[],
  changeErrorIds?: string[],
};

export default function ProductRow({
  item,
  showcases,
  checkedVariations = [],
  openedVariations = [],
  onToggle,
  onVariationToggle,
  onVariationsOpenToggle,
  onClick,
  checkedProducts = [],
  changes = [],
  changeSuccessIds = [],
  changeErrorIds = [],
}: Props): React.Node {
  const product = item;
  const checked = checkedProducts.includes(product.id);
  const variationsOpened = openedVariations.includes(product.id);
  const changed = changes.some((c) => !c.ignore && c.productId === product.id);
  const success = changeSuccessIds.includes(product.id);
  const error = changeErrorIds.includes(product.id);

  const [hover, setHover] = React.useState(false);
  const productName = product.title || product.name || 'Product';
  const quantity = product.totalQuantity || product.quantity || 0;
  const sku = getSku(product.variations);
  const { listings, id: productId, variations } = product;
  const hasVariations = variations && variations.length > 1;

  const handleCheckboxChange = () => {
    onToggle(productId, variations ? variations.map((v) => v.id) : []);
  };

  const handleMouseEnter = () => {
    setHover(true);
  };

  const handleMouseLeave = () => {
    setHover(false);
  };

  const handleToggleVariations = (event) => {
    event.stopPropagation();
    if (onVariationsOpenToggle) {
      onVariationsOpenToggle(productId);
    }
  };

  const handleClick = React.useCallback(() => {
    if (onClick) onClick(productId);
  }, [productId, onClick]);

  return (
    <div key={productId}>
      <Row
        success={success}
        changed={changed}
        selected={checked}
        error={error}
        largePadding
        className={csx('product-row')}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <Cell
          {...flex('title')}
          className={cx(csx('cell'), csx('clickable'))}
          onClick={handleClick}
        >
          <Checkbox
            checked={checked}
            onChange={handleCheckboxChange}
            onClick={(e) => e.stopPropagation()}
            className={csx('checkbox')}
          />
          {hasVariations ? (
            <button
              type="button"
              onClick={handleToggleVariations}
              className={cx(csx('variationsButton'), {
                [csx('opened')]: variationsOpened,
              })}
            >
              <Icon name="selectNormal" />
            </button>
          ) : (
            <ThumbButton onClick={handleClick} className={csx('thumb')}>
              <Thumbnail
                src={product.pictures?.[0]?.thumbnailUrl || null}
                alt={productName}
              />
            </ThumbButton>
          )}
          <div className={csx('ellipsis')}>{productName}</div>
          {!quantity && !variations.some((v) => v.quantity) && (
            <Marker className={csx('sold-out')} mode="warning">
              {t('soldOut')}
            </Marker>
          )}
        </Cell>
        <Cell {...flex('sku')} className={cx(csx('cell'), csx('sku-cell'))}>
          {sku && sku !== 'N/A' ? (
            <ClickToCopy textToCopy={sku}>{ellipsisize(sku, 7)}</ClickToCopy>
          ) : null}
        </Cell>
        <>
          <Cell {...flex('amount')} className={csx('cell')}>
            <div className={csx('quantity-display')}>{quantity}</div>
          </Cell>
          {listings &&
            showcases &&
            showcases.map(({ id: sId, site, name }) => {
              // 首先尝试通过 showcase.id 查找
              let listing = listings.find(
                (s) => s.showcase && s.showcase.id === sId
              );

              // 如果通过 showcase.id 找不到，尝试通过 site.id 查找
              // 这样可以处理不同账号使用不同 showcase ID 的情况
              if (!listing && site && site.id !== undefined) {
                listing = listings.find((s) => s.site && s.site.id === site.id);
              }

              // listings from sites like ebay.motors. These sites do not have showcases in ebaymag
              let listingWithoutShowcase = false;
              if (site && site.domain === 'ebay.com' && listings.length > 9) {
                const motorsListing = listings.find(
                  (s) => s.site && s.site.id === 100
                );

                if (motorsListing) {
                  listingWithoutShowcase = true;
                  listing = motorsListing;
                }
              }

              // 如果没有找到 listing，创建一个默认的 listing 对象
              // 这样用户可以通过切换 switcher 来将商品发布到该站点
              if (!listing) {
                listing = {
                  id: `${productId}-${sId}`,
                  published: false,
                  selected: false,
                  managed: true,
                  synchronizable: true,
                  notSynchronizableReasons: [],
                  showcase: { id: sId },
                  site,
                  problems: [],
                };
              }

              const switcher = (
                <AiModalPublishSwitcher
                  listingWithoutShowcase={listingWithoutShowcase}
                  synchronizable={listing.synchronizable}
                  notSynchronizableReasons={listing.notSynchronizableReasons}
                  published={listing.published}
                  selected={listing.selected}
                  hover={hover}
                  productId={productId}
                  showcaseId={sId}
                  managed={listing.managed}
                  domain={listingWithoutShowcase ? 'ebay.motors' : name}
                />
              );

              const { hasError, hasWarningOnly } = showWarningIcon(listing);

              return (
                <Cell
                  key={sId}
                  className={csx('cell')}
                  visibleOverflow
                  {...flex('listing')}
                >
                  {hasError || hasWarningOnly ? (
                    <Problem
                      uncritical={!listing.published}
                      problems={listing.problems}
                    />
                  ) : (
                    switcher
                  )}
                </Cell>
              );
            })}
        </>
      </Row>
      {variationsOpened &&
        hasVariations &&
        variations.map((variation) => (
          <VariationRow
            key={`v${variation.id}`}
            variation={variation}
            onClick={handleClick}
            checked={checkedVariations.includes(variation.id)}
            onToggle={(id) => {
              if (onVariationToggle) {
                onVariationToggle(id);
              }
            }}
            productId={productId}
            showcasesLength={showcases.length}
            changed={changed}
            success={success}
            error={error}
          />
        ))}
    </div>
  );
}
